import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { formatEatTime } from '@wodi/domain';
import { Button, LoadingBlock, Notice, PageHeader } from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { startConsultation, workspaceQuery } from '../api/consultation.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { VisitLabelsButton } from '../privacy/visit-labels.tsx';
import styles from './consultation.module.css';
import { DiagnosesPanel } from './diagnoses-panel.tsx';
import { CloseVisitDialog, FinishPanel } from './finish-panel.tsx';
import { NotePanel } from './note-panel.tsx';
import { OrdersPanel } from './orders-panel.tsx';
import { SummaryPanel, VitalsToday } from './summary-panel.tsx';
import { WORKSPACE_PANELS } from './workspace-panels.tsx';

// The consultation workspace (docs/plans/phase1-consultation.md §7.1; DESIGN.md §6.3, §8):
// the banner, vitals today and the patient summary, then the note on the left and
// diagnoses, orders and results, and prescriptions on the right. The address carries the
// visit's internal ID only.

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export function ConsultationWorkspacePage({ visitId }: { visitId: string }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const online = useOnline();
  const workspace = useQuery(workspaceQuery(visitId));
  const [closing, setClosing] = useState(false);
  const [requestId] = useState(() => crypto.randomUUID());
  const start = useMutation({
    mutationFn: (queueEntryId: string) =>
      startConsultation(visitId, { clientRequestId: requestId, queueEntryId }),
    onSuccess: (next) => {
      queryClient.setQueryData(['visits', visitId, 'workspace'], next);
      void queryClient.invalidateQueries({ queryKey: ['queues'] });
    },
  });

  if (session === null || !can(session, 'clinical.read')) {
    return (
      <Notice tone="info">
        You don't have permission to see consultations. Ask an administrator if you need it.
      </Notice>
    );
  }
  if (workspace.isPending) {
    return (
      <div className={styles.page}>
        <PageHeader title="Consultation" />
        <LoadingBlock label="Loading the consultation" rows={8} />
      </div>
    );
  }
  if (workspace.isError) {
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void workspace.refetch()}>
            Try again
          </Button>
        }
      >
        {errorText(workspace.error, 'The consultation could not be loaded. Try again.')}
      </Notice>
    );
  }

  const view = workspace.data;
  const mayRecord = can(session, 'consultation.record');
  const mine = view.consultations.find((c) => c.id === view.myConsultationId);
  const latest = view.consultations.at(-1);
  const shown = mine ?? latest;
  const writing = mine !== undefined && mayRecord;
  const open = view.visit.status === 'open';
  // An inpatient is seen on a ward round, and their visit closes at discharge
  // (docs/plans/phase2-ward-rounds-nursing.md §4.1).
  const inpatient = view.visit.type === 'inpatient';

  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={view.visit.patientId} />
      <PageHeader
        title="Consultation"
        description={`${
          shown === undefined
            ? 'Not started'
            : `${shown.servicePoint?.name ?? shown.ward?.name ?? ''} · ${shown.clinician.displayName} · started ${formatEatTime(new Date(shown.startedAt))}${shown.status === 'finished' ? ' · finished' : ''}`
        }${open ? '' : ` · Visit ${view.visit.status}`}`}
        actions={
          <>
            {mine === undefined && mayRecord && open && view.queueEntry !== null ? (
              <Button
                variant="primary"
                isDisabled={!online}
                isPending={start.isPending}
                pendingLabel="Starting…"
                onPress={() => {
                  start.mutate(view.queueEntry?.id ?? '');
                }}
              >
                Start consultation
              </Button>
            ) : null}
            {open && !inpatient && can(session, 'visits.close') ? (
              <Button
                variant="quiet"
                isDisabled={!online}
                onPress={() => {
                  setClosing(true);
                }}
              >
                Close visit
              </Button>
            ) : null}
            {open && !inpatient && can(session, 'inpatient.admit') ? (
              <Link to="/visits/$visitId/admit" params={{ visitId: view.visit.id }}>
                Admit
              </Link>
            ) : null}
            {open && can(session, 'theatre.book') ? (
              <Link to="/visits/$visitId/theatre-booking" params={{ visitId: view.visit.id }}>
                Book theatre
              </Link>
            ) : null}
            {open && view.visit.type === 'emergency' && can(session, 'deaths.record') ? (
              <Link to="/visits/$visitId/record-death" params={{ visitId: view.visit.id }}>
                Record death
              </Link>
            ) : null}
            {open && can(session, 'births.record') ? (
              <Link to="/visits/$visitId/record-birth" params={{ visitId: view.visit.id }}>
                Record birth
              </Link>
            ) : null}
            {/* Sensitivity labels (docs/plans/golive-privacy-tooling.md §1.4). */}
            <VisitLabelsButton visitId={view.visit.id} />
            <Link
              to="/queues"
              search={
                view.queueEntry === null ? {} : { servicePointId: view.queueEntry.servicePointId }
              }
            >
              Back to the queue
            </Link>
          </>
        }
      />
      {inpatient ? (
        <Notice tone="info">
          This patient is admitted. Ward rounds are started from their stay, on the Wards page.
        </Notice>
      ) : null}
      {start.error === null ? null : (
        <Notice tone="error">
          {errorText(start.error, 'The consultation could not be started.')}
        </Notice>
      )}
      {!online ? (
        <Notice tone="info">
          No connection to Wodi. Your note keeps saving on this device; signing, ordering and
          finishing wait for the connection.
        </Notice>
      ) : null}
      <VitalsToday view={view} />
      {view.triage === null ? null : (
        <p className={styles.help}>
          {`Triage: ${view.triage.category.display}${view.triage.category.provisional ? ' (provisional)' : ''}. Complaint: ${view.triage.chiefComplaint}`}
        </p>
      )}
      <SummaryPanel view={view} mayWrite={writing} mayVerify={can(session, 'allergies.verify')} />
      <div className={styles.columns}>
        <div className={styles.column}>
          {shown === undefined ? (
            <p className={styles.help}>
              {mayRecord && open
                ? 'Start the consultation to write the note.'
                : 'No consultation has been started for this visit.'}
            </p>
          ) : (
            <NotePanel view={view} consultationId={shown.id} mayWrite={mayRecord} />
          )}
          {shown?.clinician.id === session.user.id && mayRecord ? (
            <FinishPanel
              view={view}
              consultationId={shown.id}
              inProgress={shown.status === 'in_progress'}
            />
          ) : null}
        </div>
        <div
          className={`${styles.column} ${styles.side}`}
          aria-label="Diagnoses, orders and prescriptions"
          role="region"
        >
          <DiagnosesPanel view={view} mayWrite={mayRecord} />
          <OrdersPanel view={view} consultationId={mine?.id ?? null} />
          <WORKSPACE_PANELS.prescribing view={view} consultationId={mine?.id ?? null} />
          {WORKSPACE_PANELS.documents === null ? null : (
            <WORKSPACE_PANELS.documents view={view} consultationId={mine?.id ?? null} />
          )}
        </div>
      </div>
      <CloseVisitDialog view={view} isOpen={closing} onOpenChange={setClosing} />
    </div>
  );
}
